import { useEffect, useState, type ReactNode } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';

export type SlidingPillSegment = {
  id: string;
  label: ReactNode;
};

export type SlidingPillVariant =
  | 'primary'
  | 'sub'
  | 'case-file'
  | 'case-file-sub'
  | 'surface';

const VARIANT: Record<
  SlidingPillVariant,
  {
    tray: string;
    pill: string;
    button: string;
    activeLabel: string;
    inactiveLabel: string;
    /** Active styles on the button itself (no sliding layoutId pill). */
    staticActive: string;
  }
> = {
  primary: {
    tray: 'relative flex flex-wrap gap-0.5 bg-bg-secondary border border-border-light rounded-xl p-1 w-fit',
    pill: 'absolute inset-0 rounded-lg bg-white shadow-sm',
    button: 'relative z-10 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors',
    activeLabel: 'text-text-main',
    inactiveLabel: 'text-[#64748B] hover:text-text-main',
    staticActive: 'bg-white shadow-sm text-text-main',
  },
  sub: {
    tray: 'relative flex flex-wrap gap-2',
    pill:
      'absolute inset-0 rounded-full border border-[#3B82F6] bg-[#EFF6FF]',
    button:
      'relative z-10 rounded-full border border-transparent px-3 py-1 text-xs font-semibold transition-colors',
    activeLabel: 'text-[#3B82F6]',
    inactiveLabel:
      'border-border-light bg-white text-[#64748B] hover:border-[#3B82F6] hover:text-[#3B82F6]',
    staticActive:
      'border border-[#3B82F6] bg-[#EFF6FF] text-[#3B82F6]',
  },
  'case-file': {
    tray: 'relative flex flex-wrap gap-1',
    pill:
      'absolute inset-0 rounded-lg bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.04)]',
    button:
      'case-file-tab relative z-10 min-w-[4.5rem] flex-1 rounded-lg px-3 py-2 text-[14px] font-medium transition-colors',
    activeLabel: 'text-text-main',
    inactiveLabel: 'text-[#64748B] hover:text-text-main',
    staticActive:
      'bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.04)] text-text-main',
  },
  'case-file-sub': {
    tray: 'relative flex flex-wrap gap-0.5',
    pill:
      'absolute inset-0 rounded-md bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.04)]',
    button:
      'case-file-tab case-file-tab--sub relative z-10 min-w-0 flex-1 rounded-md px-2 py-1 text-[13px] font-medium tracking-tight transition-colors',
    activeLabel: 'text-text-main',
    inactiveLabel: 'text-[#64748B] hover:text-text-main',
    staticActive:
      'bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.04)] text-text-main',
  },
  surface: {
    tray: 'relative flex flex-wrap gap-0.5 bg-gray-100 rounded-xl p-1 w-fit',
    pill: 'absolute inset-0 rounded-lg bg-white shadow-sm',
    button: 'relative z-10 rounded-lg px-4 py-1.5 text-sm font-bold transition-colors',
    activeLabel: 'text-primary',
    inactiveLabel: 'text-gray-500 hover:text-gray-900',
    staticActive: 'bg-white shadow-sm text-primary',
  },
};

const LIQUID_SPRING = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 34,
  mass: 0.82,
};

/** Wait for route + section reveals before enabling layoutId slide (avoids fly-in glitches). */
const SLIDE_ENABLE_MS = 420;

type Props = {
  value: string;
  onChange: (id: string) => void;
  segments: SlidingPillSegment[];
  variant?: SlidingPillVariant;
  /** Unique per control on the same page so pills do not cross-animate. */
  layoutId?: string;
  /** Paint active state on the button — no layoutId slide (avoids glitch inside page transitions). */
  staticHighlight?: boolean;
  className?: string;
};

export function SlidingPillSegmentedControl({
  value,
  onChange,
  segments,
  variant = 'primary',
  layoutId = 'sliding-pill-indicator',
  staticHighlight = false,
  className = '',
}: Props) {
  const reduceMotion = useReducedMotion();
  const styles = VARIANT[variant];
  const transition = reduceMotion ? { duration: 0 } : LIQUID_SPRING;
  const [slideReady, setSlideReady] = useState(staticHighlight || reduceMotion);

  useEffect(() => {
    if (staticHighlight || reduceMotion) {
      setSlideReady(true);
      return;
    }
    setSlideReady(false);
    const timer = window.setTimeout(() => setSlideReady(true), SLIDE_ENABLE_MS);
    return () => window.clearTimeout(timer);
  }, [staticHighlight, reduceMotion, layoutId]);

  const useSlidingPill = !staticHighlight && slideReady;

  return (
    <LayoutGroup id={layoutId}>
      <motion.div className={`${styles.tray} ${className}`.trim()} layout={false}>
        {segments.map((seg) => {
          const active = value === seg.id;
          const paintStaticActive = active && (staticHighlight || !useSlidingPill);
          const isCaseFileVariant =
            variant === 'case-file' || variant === 'case-file-sub';
          return (
            <button
              key={seg.id}
              type="button"
              onClick={() => onChange(seg.id)}
              className={`${styles.button} ${
                paintStaticActive
                  ? `${styles.staticActive}${isCaseFileVariant ? ' case-file-tab--active' : ''}`
                  : active
                    ? styles.activeLabel
                    : styles.inactiveLabel
              }`}
            >
              {active && useSlidingPill && (
                <motion.div
                  layoutId={layoutId}
                  className={`${styles.pill}${isCaseFileVariant ? ' case-file-tab-pill' : ''}`}
                  transition={transition}
                  aria-hidden
                />
              )}
              <span className="relative z-10">{seg.label}</span>
            </button>
          );
        })}
      </motion.div>
    </LayoutGroup>
  );
}
